/**
 * OneSignal Manager — Centralized wrapper for all OneSignal SDK interactions.
 * Detects WebToNative (WTN) Android shell and uses native OneSignal bridge.
 * Falls back to OneSignal Web SDK for browser users.
 * App ID: ae2d4b19-946f-4ad3-a136-5ed2a1ccfa4c
 */

const ONESIGNAL_APP_ID = process.env.REACT_APP_ONESIGNAL_APP_ID;

let initialized = false;

/** Check if running inside WebToNative shell with OneSignal native bridge */
function isWTN() {
  return typeof window !== 'undefined' && !!window.WTN?.OneSignal;
}

// ══════════════════════════════════════════════════════════════
// WTN NATIVE BRIDGE — uses window.WTN.OneSignal.* APIs
// ══════════════════════════════════════════════════════════════

async function initWTN() {
  if (initialized) return;
  try {
    // Register for push notifications via WTN native bridge
    await new Promise((resolve) => {
      window.WTN.registerNotification({
        callback: (response) => {
          console.log('[OneSignal/WTN] Registered:', response.permissionStatus, 'playerId:', response.oneSignalPlayerId);
          if (response.oneSignalPlayerId) {
            try { localStorage.setItem('onesignal_player_id', response.oneSignalPlayerId); } catch {}
          }
          resolve(response);
        }
      });
      // Timeout fallback — WTN callback might not fire on denied
      setTimeout(() => resolve({ permissionStatus: 'TIMEOUT' }), 5000);
    });
    initialized = true;
    console.log('[OneSignal/WTN] Initialized');
  } catch (err) {
    console.error('[OneSignal/WTN] Init failed:', err);
  }
}

async function loginWTN(externalId) {
  if (!externalId) return;
  try {
    window.WTN.OneSignal.setExternalUserId(externalId);
    console.log('[OneSignal/WTN] External user ID set:', externalId.slice(0, 8));
  } catch (err) {
    console.error('[OneSignal/WTN] setExternalUserId failed:', err);
  }
}

async function logoutWTN() {
  try {
    window.WTN.OneSignal.removeExternalUserId();
    console.log('[OneSignal/WTN] External user ID removed');
  } catch (err) {
    console.error('[OneSignal/WTN] removeExternalUserId failed:', err);
  }
}

async function setEmailWTN(email) {
  if (!email) return;
  try {
    window.WTN.OneSignal.setEmail({ emailId: email });
    console.log('[OneSignal/WTN] Email set');
  } catch (err) {
    console.error('[OneSignal/WTN] setEmail failed:', err);
  }
}

async function setTagWTN(key, value) {
  try {
    window.WTN.OneSignal.setTags({ tags: { [key]: value } });
  } catch (err) {
    console.error('[OneSignal/WTN] setTags failed:', err);
  }
}

async function requestPermissionWTN() {
  try {
    window.WTN.OneSignal.optInUser();
    console.log('[OneSignal/WTN] optInUser called');
  } catch (err) {
    console.error('[OneSignal/WTN] optInUser failed:', err);
  }
}

// ══════════════════════════════════════════════════════════════
// WEB SDK — uses react-onesignal for browser users
// ══════════════════════════════════════════════════════════════

async function initWeb() {
  if (initialized) return;
  if (typeof window === 'undefined') return;
  if (!ONESIGNAL_APP_ID) return;

  try {
    const OneSignal = (await import('react-onesignal')).default;

    await OneSignal.init({
      appId: ONESIGNAL_APP_ID,
      allowLocalhostAsSecureOrigin: process.env.NODE_ENV === 'development',
      serviceWorkerPath: '/push/onesignal/OneSignalSDKWorker.js',
      serviceWorkerParam: { scope: '/push/onesignal/' },
      notifyButton: { enable: false },
    });

    initialized = true;
    console.log('[OneSignal/Web] Initialized');

    // Monitor subscription changes
    OneSignal.User.PushSubscription.addEventListener('change', (event) => {
      const subId = event?.current?.id;
      if (subId && !subId.startsWith('local-')) {
        console.log('[OneSignal/Web] Subscription ID:', subId);
        try { localStorage.setItem('onesignal_subscription_id', subId); } catch {}
      }
    });

    const currentId = OneSignal.User.PushSubscription.id;
    if (currentId && !currentId.startsWith('local-')) {
      try { localStorage.setItem('onesignal_subscription_id', currentId); } catch {}
    }
  } catch (err) {
    console.error('[OneSignal/Web] Init failed:', err);
  }
}

async function loginWeb(externalId) {
  if (!initialized || !externalId) return;
  try {
    const OneSignal = (await import('react-onesignal')).default;
    await OneSignal.login(externalId);
    console.log('[OneSignal/Web] Logged in:', externalId.slice(0, 8));
  } catch (err) {
    console.error('[OneSignal/Web] Login failed:', err);
  }
}

async function logoutWeb() {
  if (!initialized) return;
  try {
    const OneSignal = (await import('react-onesignal')).default;
    await OneSignal.logout();
  } catch (err) {
    console.error('[OneSignal/Web] Logout failed:', err);
  }
}

async function setEmailWeb(email) {
  if (!initialized || !email) return;
  try {
    const OneSignal = (await import('react-onesignal')).default;
    OneSignal.User.addEmail(email);
  } catch (err) {
    console.error('[OneSignal/Web] Set email failed:', err);
  }
}

async function setTagWeb(key, value) {
  if (!initialized) return;
  try {
    const OneSignal = (await import('react-onesignal')).default;
    OneSignal.User.addTag(key, value);
  } catch (err) {
    console.error('[OneSignal/Web] Set tag failed:', err);
  }
}

async function requestPermissionWeb() {
  if (!initialized) return;
  try {
    const OneSignal = (await import('react-onesignal')).default;
    await OneSignal.Notifications.requestPermission();
    console.log('[OneSignal/Web] Permission requested');
  } catch (err) {
    console.error('[OneSignal/Web] Permission request failed:', err);
  }
}

// ══════════════════════════════════════════════════════════════
// PUBLIC API — automatically routes to WTN or Web
// ══════════════════════════════════════════════════════════════

export async function initOneSignal() {
  return isWTN() ? initWTN() : initWeb();
}

export async function oneSignalLogin(externalId) {
  return isWTN() ? loginWTN(externalId) : loginWeb(externalId);
}

export async function oneSignalLogout() {
  return isWTN() ? logoutWTN() : logoutWeb();
}

export async function setOneSignalEmail(email) {
  return isWTN() ? setEmailWTN(email) : setEmailWeb(email);
}

export async function setOneSignalTag(key, value) {
  return isWTN() ? setTagWTN(key, value) : setTagWeb(key, value);
}

export async function setOneSignalNotificationMode(mode) {
  return setOneSignalTag('notification_mode', mode);
}

export async function requestPushPermission() {
  return isWTN() ? requestPermissionWTN() : requestPermissionWeb();
}
